/**
 * Subscription Error Handling Utilities
 * 
 * Centralized error handling for Stripe/subscription operations
 * Provides custom error classes, user-friendly messages, and retry logic
 */

import { api, ApiRequestError } from '@/lib/api'

export enum SubscriptionErrorType {
    PAYMENT_FAILED = 'PAYMENT_FAILED',
    SUBSCRIPTION_EXPIRED = 'SUBSCRIPTION_EXPIRED',
    SUBSCRIPTION_CANCELED = 'SUBSCRIPTION_CANCELED',
    INVALID_PLAN = 'INVALID_PLAN',
    CARD_DECLINED = 'CARD_DECLINED',
    NETWORK_ERROR = 'NETWORK_ERROR',
    API_ERROR = 'API_ERROR',
    UNKNOWN_ERROR = 'UNKNOWN_ERROR',
    SESSION_EXPIRED = 'SESSION_EXPIRED',
    RATE_LIMITED = 'RATE_LIMITED',
}

export class SubscriptionError extends Error {
    constructor(
        public type: SubscriptionErrorType,
        message: string,
        public originalError?: unknown,
        public retryable: boolean = false
    ) {
        super(message)
        this.name = 'SubscriptionError'
        Object.setPrototypeOf(this, SubscriptionError.prototype)
    }
}

/**
 * Map backend API errors to user-friendly messages
 */
const ERROR_MESSAGES: Record<SubscriptionErrorType, string> = {
    [SubscriptionErrorType.PAYMENT_FAILED]:
        'Pagamento falhou. Verifique seus dados de pagamento e tente novamente.',
    [SubscriptionErrorType.SUBSCRIPTION_EXPIRED]:
        'Sua assinatura expirou. Renove para continuar usando os recursos premium.',
    [SubscriptionErrorType.SUBSCRIPTION_CANCELED]:
        'Esta assinatura foi cancelada.',
    [SubscriptionErrorType.INVALID_PLAN]:
        'Plano inválido selecionado. Por favor, escolha um plano válido.',
    [SubscriptionErrorType.CARD_DECLINED]:
        'Cartão recusado. Verifique os dados do cartão ou use outro método de pagamento.',
    [SubscriptionErrorType.NETWORK_ERROR]:
        'Erro de conexão. Verifique sua internet e tente novamente.',
    [SubscriptionErrorType.API_ERROR]:
        'Erro no servidor. Por favor, tente novamente em alguns instantes.',
    [SubscriptionErrorType.UNKNOWN_ERROR]:
        'Ocorreu um erro inesperado. Por favor, tente novamente.',
    [SubscriptionErrorType.SESSION_EXPIRED]:
        'Sessão expirada. Por favor, faça login novamente.',
    [SubscriptionErrorType.RATE_LIMITED]:
        'Muitas tentativas. Por favor, aguarde alguns minutos antes de tentar novamente.',
}

/**
 * Parse error from API response and create SubscriptionError
 */
export function parseSubscriptionError(error: unknown): SubscriptionError {
    // Network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
        return new SubscriptionError(
            SubscriptionErrorType.NETWORK_ERROR,
            ERROR_MESSAGES[SubscriptionErrorType.NETWORK_ERROR],
            error,
            true
        )
    }

    // API errors with structured response
    if (error instanceof Error) {
        const message = error.message.toLowerCase()

        // Payment errors
        if (message.includes('payment') || message.includes('card')) {
            if (message.includes('declined') || message.includes('insufficient')) {
                return new SubscriptionError(
                    SubscriptionErrorType.CARD_DECLINED,
                    ERROR_MESSAGES[SubscriptionErrorType.CARD_DECLINED],
                    error,
                    false
                )
            }
            return new SubscriptionError(
                SubscriptionErrorType.PAYMENT_FAILED,
                ERROR_MESSAGES[SubscriptionErrorType.PAYMENT_FAILED],
                error,
                true
            )
        }

        // Subscription errors
        if (message.includes('expired')) {
            return new SubscriptionError(
                SubscriptionErrorType.SUBSCRIPTION_EXPIRED,
                ERROR_MESSAGES[SubscriptionErrorType.SUBSCRIPTION_EXPIRED],
                error,
                false
            )
        }

        if (message.includes('canceled') || message.includes('cancelled')) {
            return new SubscriptionError(
                SubscriptionErrorType.SUBSCRIPTION_CANCELED,
                ERROR_MESSAGES[SubscriptionErrorType.SUBSCRIPTION_CANCELED],
                error,
                false
            )
        }

        // Rate limiting
        if (message.includes('rate limit') || message.includes('429')) {
            return new SubscriptionError(
                SubscriptionErrorType.RATE_LIMITED,
                ERROR_MESSAGES[SubscriptionErrorType.RATE_LIMITED],
                error,
                true
            )
        }

        // Session errors
        if (message.includes('unauthorized') || message.includes('401')) {
            return new SubscriptionError(
                SubscriptionErrorType.SESSION_EXPIRED,
                ERROR_MESSAGES[SubscriptionErrorType.SESSION_EXPIRED],
                error,
                false
            )
        }

        // Invalid plan
        if (message.includes('plan') || message.includes('invalid')) {
            return new SubscriptionError(
                SubscriptionErrorType.INVALID_PLAN,
                ERROR_MESSAGES[SubscriptionErrorType.INVALID_PLAN],
                error,
                false
            )
        }

        // Generic API error
        if (message.includes('api error') || message.includes('500')) {
            return new SubscriptionError(
                SubscriptionErrorType.API_ERROR,
                ERROR_MESSAGES[SubscriptionErrorType.API_ERROR],
                error,
                true
            )
        }
    }

    // Unknown error
    return new SubscriptionError(
        SubscriptionErrorType.UNKNOWN_ERROR,
        ERROR_MESSAGES[SubscriptionErrorType.UNKNOWN_ERROR],
        error,
        true
    )
}

/**
 * Get user-friendly error message
 */
export function getErrorMessage(error: unknown): string {
    if (error instanceof SubscriptionError) {
        return error.message
    }

    const parsedError = parseSubscriptionError(error)
    return parsedError.message
}

/**
 * Check if error is retryable
 */
export function isRetryableError(error: unknown): boolean {
    if (error instanceof SubscriptionError) {
        return error.retryable
    }

    const parsedError = parseSubscriptionError(error)
    return parsedError.retryable
}

export function isMissingActiveSubscriptionError(error: unknown): boolean {
    return (
        error instanceof ApiRequestError &&
        error.statusCode === 404 &&
        error.message.toLowerCase().includes('no active subscription found')
    )
}

/**
 * Retry helper with exponential backoff
 */
export async function retryWithBackoff<T>(
    fn: () => Promise<T>,
    maxRetries: number = 3,
    baseDelay: number = 1000
): Promise<T> {
    let lastError: unknown

    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            return await fn()
        } catch (error) {
            lastError = error

            // Don't retry if error is not retryable
            if (!isRetryableError(error)) {
                throw parseSubscriptionError(error)
            }

            // Don't delay on last attempt
            if (attempt < maxRetries - 1) {
                const delay = baseDelay * Math.pow(2, attempt)
                await new Promise(resolve => setTimeout(resolve, delay))
            }
        }
    }

    throw parseSubscriptionError(lastError)
}

/**
 * Log subscription error for monitoring
 */
export function logSubscriptionError(error: SubscriptionError, context?: Record<string, unknown>): void {
    const severity: 'low' | 'medium' | 'high' = (() => {
        switch (error.type) {
            case SubscriptionErrorType.CARD_DECLINED:
            case SubscriptionErrorType.RATE_LIMITED:
                return 'low'
            case SubscriptionErrorType.NETWORK_ERROR:
            case SubscriptionErrorType.SESSION_EXPIRED:
            case SubscriptionErrorType.SUBSCRIPTION_EXPIRED:
            case SubscriptionErrorType.SUBSCRIPTION_CANCELED:
            case SubscriptionErrorType.INVALID_PLAN:
            case SubscriptionErrorType.PAYMENT_FAILED:
                return 'medium'
            case SubscriptionErrorType.API_ERROR:
            case SubscriptionErrorType.UNKNOWN_ERROR:
                return 'high'
        }
    })()

    const errorLog = {
        type: error.type,
        message: error.message,
        retryable: error.retryable,
        timestamp: new Date().toISOString(),
        context,
        originalError: error.originalError instanceof Error
            ? error.originalError.message
            : String(error.originalError)
    }

    // Log to console in development
    if (import.meta.env.DEV) {
        console.error('[Subscription Error]', errorLog)
    }

    // In production, forward errors to the backend so it can alert via free webhooks
    // (avoids exposing webhook URLs in the browser)
    if (import.meta.env.PROD) {
        void api
            .logClientSubscriptionError({
                type: error.type,
                message: error.message,
                retryable: error.retryable,
                severity,
                originalError: errorLog.originalError,
                context: context ?? {},
            })
            .catch(() => {
                // Never throw from error logging
            })
    }
}
